import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { ThemeMode, ThemeSettings } from '@fly/shared';

const LS_THEME_KEY = 'theme';

const getMediaQuery = () => {
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
  ) {
    return window.matchMedia('(prefers-color-scheme: dark)');
  }
  return null;
};

const getCurrentTheme = (themeSetting: ThemeSettings) => {
  if (themeSetting === ThemeSettings.SYSTEM) {
    const mq = getMediaQuery();
    return mq && mq.matches ? ThemeMode.DARK : ThemeMode.LIGHT;
  }
  return themeSetting;
};

const applyTheme = (themeSetting: ThemeSettings) => {
  const currentTheme = getCurrentTheme(themeSetting);

  const root = document.documentElement;
  const appWrapper = document.getElementById('app-wrapper');
  root.classList.remove(ThemeMode.LIGHT.toString());
  root.classList.remove(ThemeMode.DARK.toString());
  root.classList.add(currentTheme);

  appWrapper?.setAttribute('data-theme', currentTheme);
};

const SystemThemeContext = createContext<{
  currentTheme: ThemeMode;
  themeSetting: ThemeSettings;
  setTheme: (theme: ThemeSettings) => void;
}>({
  currentTheme: ThemeMode.DARK,
  themeSetting: ThemeSettings.SYSTEM,
  setTheme: () => {},
});

export function SystemThemeProvider({
  children,
  forcedTheme,
}: {
  children: React.ReactNode;
  // When set, the provider always renders this theme and ignores the user's
  // saved setting and OS preference. `setTheme` becomes a no-op. Used by the
  // landing page, which must always be dark regardless of user/system settings.
  forcedTheme?: ThemeMode;
}) {
  const saved =
    typeof window !== 'undefined'
      ? (localStorage.getItem(LS_THEME_KEY) as ThemeSettings | null)
      : null;

  // Setting chosen by the user
  const [themeSetting, setThemeSetting] = useState<ThemeSettings>(
    forcedTheme ?? saved ?? ThemeSettings.SYSTEM
  );
  // Actual current theme
  const [currentTheme, setCurrentTheme] = useState<ThemeMode>(
    forcedTheme ?? getCurrentTheme(themeSetting)
  );

  const handleChangeThemeSetting = useCallback(
    (theme: ThemeSettings) => {
      if (forcedTheme) {
        return;
      }
      setThemeSetting(theme);
      setCurrentTheme(getCurrentTheme(theme));
      applyTheme(theme);
      localStorage.setItem(LS_THEME_KEY, theme);
    },
    [forcedTheme]
  );

  useEffect(() => {
    if (forcedTheme) {
      applyTheme(forcedTheme);
      return;
    }

    const handlePreferenceChange = () => {
      const savedThemeSetting =
        (localStorage.getItem(LS_THEME_KEY) as ThemeSettings | null) ||
        ThemeSettings.SYSTEM;

      if (savedThemeSetting === 'system') {
        setCurrentTheme(getCurrentTheme(ThemeSettings.SYSTEM));
        applyTheme(ThemeSettings.SYSTEM);
        return;
      }
    };

    handlePreferenceChange();

    const mq = getMediaQuery();
    mq?.addEventListener('change', handlePreferenceChange);

    return () => {
      mq?.removeEventListener('change', handlePreferenceChange);
    };
  }, [forcedTheme]);

  return (
    <SystemThemeContext.Provider
      value={{ currentTheme, themeSetting, setTheme: handleChangeThemeSetting }}
    >
      {children}
    </SystemThemeContext.Provider>
  );
}

export function useSystemTheme() {
  return useContext(SystemThemeContext);
}
